import { alpha, Theme } from '@mui/material/styles'

/**
 * Centralized dialog styles
 */

interface DialogStyleOptions {
  maxWidth?: string
  minHeight?: string
  maxHeight?: string
  padding?: string
  theme?: Theme
  disabled?: boolean
  value?: any
}

/**
 * Standard dialog container styles
 * Supports both (theme, options) and ({ theme, ...options }) call signatures
 */
export const dialogStyles = (themeOrOptions: Theme | DialogStyleOptions, options: DialogStyleOptions = {}) => {
  // Support both call signatures: (theme, options) or ({ theme, ...options })
  let theme: Theme | undefined
  let finalOptions: DialogStyleOptions

  if (themeOrOptions && typeof themeOrOptions === 'object' && 'theme' in themeOrOptions) {
    // Called as ({ theme, disabled, value, ... })
    theme = themeOrOptions.theme
    finalOptions = themeOrOptions
  } else {
    // Called as (theme, options)
    theme = themeOrOptions as Theme
    finalOptions = options
  }

  const { maxWidth = '800px', minHeight = '50vh', maxHeight = '95vh', padding = '20px' } = finalOptions

  return {
    width: '100%',
    maxWidth,
    minHeight,
    maxHeight,
    padding,
    backgroundColor: theme?.palette?.background?.section,
    color: theme?.palette?.text?.primary,
    borderRadius: '8px',
    boxShadow: `0 4px 20px ${alpha(theme?.palette?.text?.primary || 'rgba(0,0,0,1)', 0.3)}`,
  }
}

/**
 * Dialog title styles
 */
export const dialogTitleStyles = (theme: Theme) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  // marginBottom: '16px',
  color: theme.palette.text.primary,
  // borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
  paddingBottom: '12px',
})

/**
 * Dialog content styles
 */
export const dialogContentStyles = (theme: Theme) => ({
  flex: 1,
  overflowY: 'auto' as const,
  padding: '16px 0',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    boxShadow: `inset 0 0 6px ${alpha(theme.palette.text.primary, 0.15)}`,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.text.primary, 0.5),
    borderRadius: '6px',
  },
})

/**
 * Dialog actions (footer) styles
 */
export const dialogActionsStyles = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '24px',
  paddingTop: '16px',
  borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
})

/**
 * Large dialog variant (for forms, detailed views)
 */
export const largeDialogStyles = (theme: Theme) =>
  dialogStyles(theme, {
    maxWidth: '1200px',
    minHeight: '60vh',
    maxHeight: '90vh',
    padding: '30px',
  })

/**
 * Small dialog variant (for confirmations, alerts)
 */
export const smallDialogStyles = (theme: Theme) =>
  dialogStyles(theme, {
    maxWidth: '400px',
    minHeight: '200px',
    maxHeight: '400px',
    padding: '20px',
  })

/**
 * Full-screen dialog overlay styles
 */
export const dialogOverlayStyles = (_theme: Theme) => ({
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
  backdropFilter: 'blur(4px)',
})
